/**
 * R4 stage model on the frontend (Specs/2026-10-13-r4-shell/plan.md §2.7, §4).
 *
 * A bid has a `lifecycle` (lead, active, lodged, closed), a `stage` while it is
 * active, and an `outcome` once it is closed. A backend that predates the
 * stage model sends only `status`, so every reader goes through
 * `bidLifecycleOf`, which derives the new fields from `status` when they are
 * missing. The mapping mirrors `backend/src/lib/bid-lifecycle-mapping.ts`,
 * except that `drafting` reads as Write here: the frontend has no document
 * facts to tell Plan from Write, and Write is the safer label to show.
 */

import type { Bid, BidStatus } from "@/lib/schemas"

// ===================
// Values and labels
// ===================

export const BID_LIFECYCLES = ["lead", "active", "lodged", "closed"] as const
export type BidLifecycle = (typeof BID_LIFECYCLES)[number]

export const BID_STAGES = ["qualify", "plan", "write", "review", "submit"] as const
export type BidStage = (typeof BID_STAGES)[number]

export const BID_OUTCOMES = ["won", "lost", "no_go", "withdrawn", "cancelled"] as const
export type BidOutcomeValue = (typeof BID_OUTCOMES)[number]

/** UI names. `lodged` reads as "Submitted": that is the word bid teams use. */
export const LIFECYCLE_LABELS: Record<BidLifecycle, string> = {
  lead: "Lead",
  active: "Active",
  lodged: "Submitted",
  closed: "Closed",
}

/** Plural tab names on the Bids list. */
export const LIFECYCLE_TAB_LABELS: Record<BidLifecycle, string> = {
  active: "Active",
  lead: "Leads",
  lodged: "Submitted",
  closed: "Closed",
}

/** Tab order on the Bids list. */
export const LIFECYCLE_TAB_ORDER: readonly BidLifecycle[] = ["active", "lead", "lodged", "closed"]

export const STAGE_LABELS: Record<BidStage, string> = {
  qualify: "Qualify",
  plan: "Plan",
  write: "Write",
  review: "Review",
  submit: "Submit",
}

export const OUTCOME_LABELS: Record<BidOutcomeValue, string> = {
  won: "Won",
  lost: "Lost",
  no_go: "No go",
  withdrawn: "Withdrawn",
  cancelled: "Cancelled by the buyer",
}

/** Short form for pills and table cells, where "Cancelled by the buyer" is too long. */
export const OUTCOME_SHORT_LABELS: Record<BidOutcomeValue, string> = {
  ...OUTCOME_LABELS,
  cancelled: "Cancelled",
}

/** Score, rank, bidders, reason and debrief describe a result: only won or lost has one. */
export function outcomeHasResult(outcome: BidOutcomeValue | null | undefined): boolean {
  return outcome === "won" || outcome === "lost"
}

export function isBidStage(value: unknown): value is BidStage {
  return typeof value === "string" && (BID_STAGES as readonly string[]).includes(value)
}

export function isBidLifecycle(value: unknown): value is BidLifecycle {
  return typeof value === "string" && (BID_LIFECYCLES as readonly string[]).includes(value)
}

export function isBidOutcome(value: unknown): value is BidOutcomeValue {
  return typeof value === "string" && (BID_OUTCOMES as readonly string[]).includes(value)
}

// ===================
// Deriving the fields
// ===================

export interface BidLifecycleFields {
  lifecycle: BidLifecycle
  /** Set only while the bid is active. */
  stage: BidStage | null
  /** Set only when the bid is closed. */
  outcome: BidOutcomeValue | null
}

/** The fields a reader needs: `status` always, the new fields when the backend sends them. */
export type LifecycleSource = Pick<Bid, "status"> &
  Partial<Pick<Bid, "lifecycle" | "stage" | "outcome">>

/** Today's `status` as the new fields (plan §4.2, with `drafting` read as Write). */
export function lifecycleFromStatus(status: BidStatus): BidLifecycleFields {
  switch (status) {
    case "lead":
      return { lifecycle: "lead", stage: null, outcome: null }
    case "qualifying":
      return { lifecycle: "active", stage: "qualify", outcome: null }
    case "drafting":
      return { lifecycle: "active", stage: "write", outcome: null }
    case "submitted":
      return { lifecycle: "lodged", stage: null, outcome: null }
    case "won":
    case "lost":
    case "no_go":
      return { lifecycle: "closed", stage: null, outcome: status }
    default:
      // A status this build does not know (a legacy value, a partial mock):
      // read it as a lead, which claims nothing about the bid's progress.
      return { lifecycle: "lead", stage: null, outcome: null }
  }
}

/**
 * The compatible `status` for a set of new fields (plan §4.3). Withdrawn is
 * written as `no_go` and Cancelled as `lost`, as the backend does.
 */
export function statusFromLifecycle(fields: BidLifecycleFields): BidStatus {
  switch (fields.lifecycle) {
    case "lead":
      return "lead"
    case "active":
      return fields.stage === "qualify" || fields.stage === null ? "qualifying" : "drafting"
    case "lodged":
      return "submitted"
    case "closed":
      switch (fields.outcome) {
        case "won":
          return "won"
        case "lost":
        case "cancelled":
          return "lost"
        default:
          return "no_go"
      }
  }
}

/**
 * Where a bid is. Uses the stored fields when the backend sends them, and
 * derives them from `status` when it does not (a backend before migration 201).
 */
export function bidLifecycleOf(bid: LifecycleSource): BidLifecycleFields {
  if (isBidLifecycle(bid.lifecycle)) {
    const lifecycle = bid.lifecycle
    const stage = lifecycle === "active" && isBidStage(bid.stage) ? bid.stage : null
    const outcome = lifecycle === "closed" && isBidOutcome(bid.outcome) ? bid.outcome : null
    // An active bid always has a stage, and a closed one an outcome. A row that
    // breaks that (a half-written response) falls back to what status says.
    if (lifecycle === "active" && stage === null) return lifecycleFromStatus(bid.status)
    if (lifecycle === "closed" && outcome === null) return lifecycleFromStatus(bid.status)
    return { lifecycle, stage, outcome }
  }
  return lifecycleFromStatus(bid.status)
}

/** A bid still in play: a lead or an active bid. */
export function isOpenLifecycle(lifecycle: BidLifecycle): boolean {
  return lifecycle === "lead" || lifecycle === "active"
}

/** One label for where a bid is: the stage, "Submitted", the outcome, or "Lead". */
export function bidPositionLabel(fields: BidLifecycleFields, { short = true } = {}): string {
  switch (fields.lifecycle) {
    case "lead":
      return LIFECYCLE_LABELS.lead
    case "active":
      return fields.stage ? STAGE_LABELS[fields.stage] : LIFECYCLE_LABELS.active
    case "lodged":
      return LIFECYCLE_LABELS.lodged
    case "closed":
      if (!fields.outcome) return LIFECYCLE_LABELS.closed
      return short ? OUTCOME_SHORT_LABELS[fields.outcome] : OUTCOME_LABELS[fields.outcome]
  }
}

/** Sort key for the Stage column: Leads, the five stages, Submitted, then outcomes. */
export function bidPositionRank(fields: BidLifecycleFields): number {
  switch (fields.lifecycle) {
    case "lead":
      return 0
    case "active":
      return 1 + (fields.stage ? BID_STAGES.indexOf(fields.stage) : 0)
    case "lodged":
      return 10
    case "closed":
      return 20 + (fields.outcome ? BID_OUTCOMES.indexOf(fields.outcome) : 0)
  }
}

/** The new fields for a bid whose status was just set by old code paths (harvest, board). */
export function withStatus<T extends LifecycleSource>(bid: T, status: BidStatus): T {
  return { ...bid, status, ...lifecycleFromStatus(status) }
}

// ===================
// Lifecycle actions (plan §2.7 table)
// ===================

export type LifecycleAction =
  | "start_bidding"
  | "mark_submitted"
  | "move_to_leads"
  | "close_no_go"
  | "close_withdrawn"
  | "record_result"
  | "edit_result"
  | "reopen"

export const LIFECYCLE_ACTION_LABELS: Record<LifecycleAction, string> = {
  start_bidding: "Start bidding",
  mark_submitted: "Mark as submitted",
  move_to_leads: "Move to Leads",
  close_no_go: "Close as no-go",
  close_withdrawn: "Close as withdrawn",
  record_result: "Record result",
  edit_result: "Edit result",
  reopen: "Reopen",
}

/** The actions offered for a bid in each lifecycle, in menu order. */
export function lifecycleActionsFor(lifecycle: BidLifecycle): LifecycleAction[] {
  switch (lifecycle) {
    case "lead":
      return ["start_bidding", "close_no_go"]
    case "active":
      return ["mark_submitted", "move_to_leads", "close_no_go", "close_withdrawn"]
    case "lodged":
      return ["record_result", "reopen"]
    case "closed":
      return ["edit_result", "reopen"]
  }
}

// ===================
// Bids list tabs and counts
// ===================

/** `?tab=` values on /app/bids, one per lifecycle. */
const TAB_PARAM_VALUES: Record<BidLifecycle, string> = {
  active: "active",
  lead: "leads",
  lodged: "submitted",
  closed: "closed",
}

export const BIDS_TAB_PARAM = "tab"

/** The lifecycle a `?tab=` value names. Anything else (or nothing) is Active. */
export function parseBidsTab(value: string | null): BidLifecycle {
  const match = LIFECYCLE_TAB_ORDER.find((lifecycle) => TAB_PARAM_VALUES[lifecycle] === value)
  return match ?? "active"
}

export function bidsTabParam(lifecycle: BidLifecycle): string {
  return TAB_PARAM_VALUES[lifecycle]
}


export type LifecycleCounts = Record<BidLifecycle, number>

/** Count bids per lifecycle (client-side, for the fallback before the backend sends counts). */
export function countByLifecycle(bids: readonly LifecycleSource[]): LifecycleCounts {
  const counts: LifecycleCounts = { lead: 0, active: 0, lodged: 0, closed: 0 }
  for (const bid of bids) counts[bidLifecycleOf(bid).lifecycle]++
  return counts
}
